import { createElement } from 'react';
import { createTheme, alpha } from '@mui/material/styles';
import { CheckboxUncheckedIcon, CheckboxCheckedIcon, CheckboxIndeterminateIcon, } from './checkboxIcons';
export var theme = createTheme({
    shape: {
        borderRadius: 5,
    },
    spacing: 8,
    palette: {
        primary: {
            main: '#9FC642',
            contrastText: '#1A210B',
            100: '#FBFFF2',
            200: '#EDF7D7',
            300: '#D4E4AF',
            400: '#B8D279',
            500: '#9FC642',
            600: '#719223',
            700: '#516E10',
            800: '#344116',
            900: '#1A210B',
        },
        secondary: {
            main: '#5646D0',
            light: '#E8E6FB',
            contrastText: '#FFFFFF',
            dark: '#4C3EAF',
            100: '#F6F5FF',
            200: '#E8E6FB',
            300: '#D0CBF6',
            400: '#6656E1',
            500: '#5646D0',
            600: '#4C3EAF',
            700: '#3D3487',
            800: '#2E2765',
            900: '#241E4F',
        },
        neutral: {
            100: '#FFFFFF',
            200: '#F6F6F6',
            300: '#E7E6E6',
            400: '#C9C9C9',
            500: '#9B9B9B',
            600: '#686767',
            700: '#4F4E4E',
            800: '#1A1919',
            900: '#000000',
        },
        info: {
            main: '#E0E0E0',
            contrastText: '#3E3E3C',
        },
        text: {
            primary: '#1A1919',
            secondary: '#4F4E4E', // neutral-700
        },
        success: {
            100: '#F3FFF2',
            500: '#086600',
        },
        warning: {
            main: '#804600',
            100: '#FEF7E5',
            500: '#804600',
        },
        error: {
            main: '#A80011',
            100: '#FFF3F4',
            500: '#A80011',
        },
        link: {
            main: '#404041',
            hover: '#1A1919',
            active: '#A1A1A1',
        },
        background: {
            default: '#F5F5F5',
        },
    },
    typography: {
        fontFamily: 'Fira Sans, sans-serif',
        fontSize: 16,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
        h1: {
            fontSize: '3.75rem',
            fontWeight: 700,
            lineHeight: '5rem', // 80px
        },
        h2: {
            fontSize: '3rem',
            fontWeight: 700,
            lineHeight: '4rem', // 64px
        },
        h3: {
            fontSize: '2.25rem',
            fontWeight: 700,
            lineHeight: '2.5rem', // 40px
        },
        h4: {
            fontSize: '1.875rem',
            fontWeight: 600,
            lineHeight: '2.25rem', // 36px
        },
        h5: {
            fontSize: '1.5rem',
            fontWeight: 600,
            lineHeight: '2rem', // 32px
        },
        h6: {
            fontSize: '1.25rem',
            fontWeight: 600,
            lineHeight: '1.75rem', // 28px
        },
        subtitle1: {
            fontSize: '1.125rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
        },
        subtitle2: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
        },
        body1: {
            fontSize: '1rem',
            fontWeight: 400,
            lineHeight: '1.5rem',
        },
        body2: {
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
        },
        caption: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: '1rem',
        },
    },
    components: {
        MuiSvgIcon: {
            styleOverrides: {
                fontSizeSmall: {
                    fontSize: '1rem',
                },
                fontSizeMedium: {
                    fontSize: '2rem',
                },
                fontSizeLarge: {
                    fontSize: '3rem',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontSize: '1rem',
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'primary',
                icon: createElement(CheckboxUncheckedIcon),
                checkedIcon: createElement(CheckboxCheckedIcon),
                indeterminateIcon: createElement(CheckboxIndeterminateIcon),
            },
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return ({
                        padding: '6px',
                        borderRadius: '4px',
                        // Override global CSS input[type='checkbox'] { z-index: -1 } which blocks clicks
                        '& input[type="checkbox"]': {
                            zIndex: 1,
                        },
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.08),
                        },
                        '&.Mui-focusVisible': {
                            outline: "2px solid ".concat(theme.palette.primary.main),
                            outlineOffset: '2px',
                        },
                        '&.Mui-disabled': {
                            opacity: 0.38,
                        },
                    });
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& input[type="number"]': {
                        MozAppearance: 'textfield',
                        appearance: 'none', // Chrome, Safari
                    },
                    '& input[type="number"]::-webkit-outer-spin-button': {
                        display: 'none', // Chrome
                    },
                    '& input[type="number"]::-webkit-inner-spin-button': {
                        display: 'none', // Chrome
                    },
                },
            },
        },
    },
});
